import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTheme, Typography, Box, Breadcrumbs as MuiBreadcrumbs } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import ROUTES from 'router/routes.constants';
import { useGetFinfactorBankAccountsQuery } from 'store/api/finfactorBank.apislice';
import { useGetPortfolioHoldingsQuery } from 'store/api/portfolio.apislice';


// Breadcrumb configuration mapping
const BREADCRUMB_CONFIG = {
  [ROUTES.MUTUAL_FUNDS]: {
    label: 'Explore Mutual Funds',
    path: ROUTES.MUTUAL_FUNDS,
  },
  [ROUTES.MUTUAL_FUNDS_FUND_ID]: {
    label: 'Fund Details',
    path: null, // Dynamic route, will be handled specially
    parent: ROUTES.MUTUAL_FUNDS,
  },
  [ROUTES.PORTFOLIO]: {
    label: 'Portfolio',
    path: ROUTES.PORTFOLIO,
  },
  [ROUTES.PORTFOLIO_MUTUAL_FUND]: {
    label: 'Mutual Funds',
    path: ROUTES.PORTFOLIO_MUTUAL_FUND,
    parent: ROUTES.PORTFOLIO,
  },
  [ROUTES.PORTFOLIO_MUTUAL_FUND_FUND_ID]: {
    label: 'Fund Details',
    path: null, // Dynamic route
    parent: ROUTES.PORTFOLIO_MUTUAL_FUND,
  },
  [ROUTES.PORTFOLIO_ORDER]: {
    label: 'Transactions',
    path: ROUTES.PORTFOLIO_ORDER,
    parent: ROUTES.PORTFOLIO,
  },
  [ROUTES.PORTFOLIO_ORDER_ORDER_ID]: {
    label: 'Order Details',
    path: null, // Dynamic route
    parent: ROUTES.PORTFOLIO_ORDER,
  },
  [ROUTES.PORTFOLIO_SUMMARY]: {
    label: 'Summary',
    path: ROUTES.PORTFOLIO_SUMMARY,
    parent: ROUTES.PORTFOLIO,
  },
  [ROUTES.SIPS]: {
    label: 'Systematic Investments',
    path: ROUTES.SIPS,
    parent: ROUTES.PORTFOLIO,
  },
  [ROUTES.SIPS_ACTIVE]: {
    label: 'Active SIPs',
    path: ROUTES.SIPS_ACTIVE,
    parent: ROUTES.SIPS,
  },
  [ROUTES.SIPS_MANDATES]: {
    label: 'Mandates',
    path: ROUTES.SIPS_MANDATES,
    parent: ROUTES.SIPS,
  },
  [ROUTES.SIPS_SUMMARY]: {
    label: 'Summary',
    path: ROUTES.SIPS_SUMMARY,
    parent: ROUTES.SIPS,
  },
  [ROUTES.SIPS_SIP_ID]: {
    label: 'SIP Details',
    path: null, // Dynamic route
    parent: ROUTES.SIPS_ACTIVE,
  },
  [ROUTES.PROFILE]: {
    label: 'Profile',
    path: ROUTES.PROFILE,
  },
  [ROUTES.PROFILE_PERSONAL_DETAILS]: {
    label: 'Personal Details',
    path: ROUTES.PROFILE_PERSONAL_DETAILS,
    parent: ROUTES.PROFILE,
  },
  [ROUTES.PROFILE_NOMINEE_DETAILS]: {
    label: 'Nominee Details',
    path: ROUTES.PROFILE_NOMINEE_DETAILS,
    parent: ROUTES.PROFILE,
  },
  [ROUTES.PROFILE_BANK_DETAILS]: {
    label: 'Bank Details',
    path: ROUTES.PROFILE_BANK_DETAILS,
    parent: ROUTES.PROFILE,
  },
  [ROUTES.PROFILE_MANDATES]: {
    label: 'Mandates',
    path: ROUTES.PROFILE_MANDATES,
    parent: ROUTES.PROFILE,
  },
  [ROUTES.INVESTMENTS]: {
    label: 'My Investment',
    path: ROUTES.INVESTMENTS,
  },
  [ROUTES.FINANCIAL_OVERVIEW]: {
    label: 'Financial Overview',
    path: ROUTES.FINANCIAL_OVERVIEW,
    parent: ROUTES.INVESTMENTS,
  },
  [ROUTES.FINFACTOR_BANKING]: {
    label: 'Bank Accounts',
    path: ROUTES.FINFACTOR_BANKING,
    parent: ROUTES.FINANCIAL_OVERVIEW,
  },
  [ROUTES.FINFACTOR_INSURANCE]: {
    label: 'Insurance',
    path: ROUTES.FINFACTOR_INSURANCE,
    parent: ROUTES.FINANCIAL_OVERVIEW,
  },
  [ROUTES.INVESTMENT_PORTFOLIO]: {
    label: 'Investment Portfolio',
    path: ROUTES.INVESTMENT_PORTFOLIO,
    parent: ROUTES.INVESTMENTS,
  },
  [ROUTES.INVESTMENT_PORTFOLIO_DETAILS]: {
    label: 'Category Details',
    path: ROUTES.INVESTMENT_PORTFOLIO_DETAILS,
    parent: ROUTES.INVESTMENT_PORTFOLIO,
  },
  [ROUTES.INVESTMENT_PORTFOLIO_BROKER]: {
    label: 'Broker Details',
    path: null,
    parent: ROUTES.INVESTMENT_PORTFOLIO_DETAILS,
  },
  [ROUTES.EXPLORE]: {
    label: 'Explore',
    path: ROUTES.EXPLORE,
  },
  [ROUTES.EXPLORE_COLLECTION_DETAILS]: {
    label: 'Collection Details',
    path: null, // Dynamic route
    parent: ROUTES.EXPLORE,
  },
  [ROUTES.REPORTS]: {
    label: 'Reports',
    path: ROUTES.REPORTS,
  },
  [ROUTES.HELP_CENTER]: {
    label: 'Help Center',
    path: ROUTES.HELP_CENTER,
  },
  [ROUTES.NOTIFICATIONS]: {
    label: 'Notifications',
    path: ROUTES.NOTIFICATIONS,
  },
  [ROUTES.NOTIFICATIONS_GENERAL]: {
    label: 'General',
    path: ROUTES.NOTIFICATIONS_GENERAL,
    parent: ROUTES.NOTIFICATIONS,
  },
  [ROUTES.NOTIFICATIONS_IMPORTANT]: {
    label: 'Important',
    path: ROUTES.NOTIFICATIONS_IMPORTANT,
    parent: ROUTES.NOTIFICATIONS,
  },
  [ROUTES.STP_ROOT]: {
    label: 'STPs',
    path: ROUTES.STP_ROOT,
    parent: ROUTES.SIPS,
  },
  [ROUTES.STP_ACTIVE]: {
    label: 'Active STPs',
    path: ROUTES.STP_ACTIVE,
    parent: ROUTES.STP_ROOT,
  },
  [ROUTES.STP_DETAILS]: {
    label: 'STP Details',
    path: null,
    parent: ROUTES.STP_ACTIVE,
  },
  [ROUTES.SWP_ROOT]: {
    label: 'SWPs',
    path: ROUTES.SWP_ROOT,
    parent: ROUTES.SIPS,
  },
  [ROUTES.SWP_ACTIVE]: {
    label: 'Active SWPs',
    path: ROUTES.SWP_ACTIVE,
    parent: ROUTES.SWP_ROOT,
  },
  [ROUTES.SWP_DETAILS]: {
    label: 'SWP Details',
    path: null,
    parent: ROUTES.SWP_ACTIVE,
  },
};

const BreadcrumbNavigation = ({ customBreadcrumbs = null }) => {
  const theme = useTheme();
  const location = useLocation();
  const { data: bankData } = useGetFinfactorBankAccountsQuery();
  const { data: portfolioHoldingsResponse } = useGetPortfolioHoldingsQuery({ sortBy: 'highest_gain' });

  // Debug logging
  console.log('BreadcrumbNavigation - Current path:', location.pathname);
  console.log('BreadcrumbNavigation - Custom breadcrumbs:', customBreadcrumbs);

  // If custom breadcrumbs are provided, use them
  if (customBreadcrumbs) {
    return (
      <MuiBreadcrumbs
        separator={<ChevronRight sx={{ fontSize: 16, color: theme.palette.custom.text.helperDisabled }} />}
        sx={{
          mb: 2,
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
        }}
      >
        {customBreadcrumbs.map((breadcrumb, index) => (
          <Box key={index}>
            {breadcrumb.path ? (
              <Link
                to={breadcrumb.path}
                style={{
                  textDecoration: 'none',
                  color: theme.palette.custom.text.helperDisabled,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: theme.palette.custom.text.helperDisabled,
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {breadcrumb.label}
                </Typography>
              </Link>
            ) : (
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: theme.palette.custom.text.heading,
                }}
              >
                {breadcrumb.label}
              </Typography>
            )}
          </Box>
        ))}
      </MuiBreadcrumbs>
    );
  }

  // Generate breadcrumbs from current route
  const generateBreadcrumbs = () => {
    const breadcrumbs = [];
    const pathname = location.pathname;

    // Simple route matching based on pathname patterns
    const getBreadcrumbsForPath = (path) => {
      // Direct matches
      if (path === ROUTES.MUTUAL_FUNDS) {
        return [{ label: 'Explore Mutual Funds', path: ROUTES.MUTUAL_FUNDS }];
      }

      if (path === ROUTES.PORTFOLIO) {
        return [{ label: 'Portfolio', path: ROUTES.PORTFOLIO }];
      }

      if (path === ROUTES.SIPS || path === ROUTES.SYSTEMATIC_INVESTMENT) {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Systematic Investments', path: ROUTES.SYSTEMATIC_INVESTMENT }
        ];
      }
      if (path === ROUTES.STP_ROOT || path === ROUTES.SWP_ROOT) {
        return [];
      }

      if (path === ROUTES.PROFILE) {
        return [{ label: 'Profile', path: ROUTES.PROFILE }];
      }

      if (path === ROUTES.INVESTMENTS) {
        return [{ label: 'My Investment', path: ROUTES.INVESTMENTS }];
      }

      if (path === ROUTES.FINANCIAL_OVERVIEW) {
        return [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW }
        ];
      }

      if (path === ROUTES.FINFACTOR_BANKING) {
        return [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW },
          { label: 'Bank Accounts', path: ROUTES.FINFACTOR_BANKING }
        ];
      }

      if (path === ROUTES.FINFACTOR_INSURANCE) {
        return [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW },
          { label: 'Insurance', path: ROUTES.FINFACTOR_INSURANCE }
        ];
      }

      if (path.startsWith(ROUTES.FINFACTOR_BANKING + '/') && path !== ROUTES.FINFACTOR_BANKING) {
        const parts = path.split('/').filter(Boolean);
        const bankIdRaw = parts[2];

        // Try to get bank name from state first
        let bankLabel = location.state?.bankName;

        // Fallback: Try to find bank name in the accounts list if data is available
        if (!bankLabel && bankData?.bankAccounts) {
          const bank = bankData.bankAccounts.find(b => b.id.toString() === bankIdRaw);
          if (bank) bankLabel = bank.bankName;
        }

        // Final fallback: format the ID or use 'Bank Details'
        if (!bankLabel) {
          bankLabel = bankIdRaw?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Bank Details';
        }

        return [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW },
          { label: 'Bank Accounts', path: ROUTES.FINFACTOR_BANKING },
          { label: bankLabel, path: null }
        ];
      }

      if (path === ROUTES.INVESTMENT_PORTFOLIO) {
        return [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW },
          { label: 'Investment Portfolio', path: ROUTES.INVESTMENT_PORTFOLIO }
        ];
      }

      if (path.startsWith(ROUTES.INVESTMENT_PORTFOLIO + '/') && path !== ROUTES.INVESTMENT_PORTFOLIO) {
        const parts = path.split('/').filter(Boolean);
        const categoryRaw = parts[2]; // index 2 for /financial-overview/portfolio/:category
        const brokerIdRaw = parts[3]; // index 3 for /financial-overview/portfolio/:category/:brokerId

        const categoryLabel =
          categoryRaw === 'etf'
            ? 'ETF'
            : categoryRaw === 'stocks'
              ? 'STOCKS'
              : categoryRaw === 'invit'
                ? 'INVIT'
                : categoryRaw === 'reit'
                  ? 'REIT'
                  : categoryRaw?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Category Details';

        const crumbs = [
          { label: 'Financial Overview', path: ROUTES.FINANCIAL_OVERVIEW },
          { label: 'Investment Portfolio', path: ROUTES.INVESTMENT_PORTFOLIO },
          { label: categoryLabel, path: brokerIdRaw ? ROUTES.INVESTMENT_PORTFOLIO_DETAILS.replace(':category', categoryRaw) : null }
        ];

        if (brokerIdRaw) {
          const brokerNameFromState = location.state?.brokerName;
          const brokerLabel = brokerNameFromState || (brokerIdRaw === 'etf' ? 'ETF' : brokerIdRaw === 'stocks' ? 'STOCKS' : brokerIdRaw.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '));
          const typeParam = new URLSearchParams(location.search).get('type');

          crumbs.push({
            label: brokerLabel,
            path: (typeParam || parts[4]) ? ROUTES.INVESTMENT_PORTFOLIO_BROKER.replace(':category', categoryRaw).replace(':brokerId', brokerIdRaw) : null
          });

          if (typeParam) {
            const labelMap = { 'sip': 'SIP', 'lumpsum': 'Lumpsum', 'redeem': 'Redeem' };
            const typeLabel = labelMap[typeParam.toLowerCase()] || typeParam.charAt(0).toUpperCase() + typeParam.slice(1);
            crumbs.push({
              label: typeLabel,
              path: parts[4] ? `${ROUTES.INVESTMENT_PORTFOLIO_BROKER.replace(':category', categoryRaw).replace(':brokerId', brokerIdRaw)}?type=${typeParam}` : null
            });
          }
        }

        if (parts[4]) {
          crumbs.push({ label: 'Order Details', path: null });
        }

        return crumbs;
      }

      if (path === ROUTES.EXPLORE) {
        return [{ label: 'Explore', path: ROUTES.EXPLORE }];
      }

      if (path === ROUTES.REPORTS) {
        return [{ label: 'Reports', path: ROUTES.REPORTS }];
      }

      if (path === ROUTES.HELP_CENTER) {
        return [{ label: 'Help Center', path: ROUTES.HELP_CENTER }];
      }

      if (path === ROUTES.NOTIFICATIONS) {
        return [{ label: 'Notifications', path: ROUTES.NOTIFICATIONS }];
      }

      if (path === ROUTES.CREATE_MANDATE) {
        return [
          { label: 'SIPs', path: ROUTES.SIPS },
          { label: 'Mandates', path: ROUTES.SIPS_MANDATES },
          { label: 'Create Mandate', path: null }
        ];
      }

      // Dynamic routes with parameters
      if (path.startsWith('/mutual-funds/') && path !== '/mutual-funds') {
        return [
          { label: 'Explore Mutual Funds', path: ROUTES.MUTUAL_FUNDS },
          { label: 'Fund Details', path: null }
        ];
      }

      if (path.startsWith('/portfolio/mutual-fund/') && path.endsWith('/redeem')) {
        const parts = path.split('/').filter(Boolean);
        const fundId = parts[2];
        let fundName = location.state?.fundName || location.state?.fund?.fundName || location.state?.fund?.name || location.state?.fund?.schemeName;
        if (!fundName && portfolioHoldingsResponse?.result?.funds) {
          const matched = portfolioHoldingsResponse.result.funds.find(f => String(f.id) === String(fundId));
          if (matched) {
            fundName = matched.schemeName;
          }
        }
        if (!fundName) fundName = 'Fund Details';
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Mutual Funds', path: ROUTES.PORTFOLIO_MUTUAL_FUND },
          { label: fundName, path: `/portfolio/mutual-fund/${fundId}` },
          { label: 'Redeem', path: null }
        ];
      }

      if (path.startsWith('/portfolio/mutual-fund/') && path !== '/portfolio/mutual-fund') {
        const parts = path.split('/').filter(Boolean);
        const fundId = parts[2];
        let fundName = location.state?.fundName || location.state?.fund?.fundName || location.state?.fund?.name || location.state?.fund?.schemeName;
        if (!fundName && portfolioHoldingsResponse?.result?.funds) {
          const matched = portfolioHoldingsResponse.result.funds.find(f => String(f.id) === String(fundId));
          if (matched) {
            fundName = matched.schemeName;
          }
        }
        if (!fundName) fundName = 'Fund Details';
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Mutual Funds', path: ROUTES.PORTFOLIO_MUTUAL_FUND },
          { label: fundName, path: null }
        ];
      }

      if (path === '/portfolio/mutual-fund') {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Mutual Funds', path: ROUTES.PORTFOLIO_MUTUAL_FUND }
        ];
      }

      if (path.startsWith('/portfolio/order/') && path !== '/portfolio/order') {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Orders', path: ROUTES.PORTFOLIO_ORDER },
          { label: 'Order Details', path: null }
        ];
      }

      if (path === '/portfolio/order') {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Transactions', path: ROUTES.PORTFOLIO_ORDER }
        ];
      }

      if (path === '/portfolio/summary') {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Summary', path: ROUTES.PORTFOLIO_SUMMARY }
        ];
      }

      if (path.startsWith('/systematic-investment')) {
        const crumbs = [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Systematic Investments', path: ROUTES.SYSTEMATIC_INVESTMENT }
        ];

        if (path.includes('/sip')) {
          crumbs.push({ label: 'SIP', path: null });
        } else if (path.includes('/stp')) {
          crumbs.push({ label: 'STP', path: null });
        } else if (path.includes('/swp')) {
          crumbs.push({ label: 'SWP', path: null });
        }

        return crumbs;
      }

      if (path === ROUTES.PORTFOLIO_BOND) {
        return [
          { label: 'Portfolio', path: ROUTES.PORTFOLIO },
          { label: 'Bonds', path: ROUTES.PORTFOLIO_BOND }
        ];
      }

      if (path === '/sips/mandates' || path === ROUTES.SIPS_MANDATES) {
        return [
          { label: 'Systematic Transactions', path: ROUTES.SIPS },
          { label: 'Mandates', path: ROUTES.SIPS_MANDATES },
        ];
      }

      if (path === '/sips/summary' || path === ROUTES.SIPS_SUMMARY) {
        return [
          { label: 'Systematic Transactions', path: ROUTES.SIPS },
          { label: 'Summary', path: ROUTES.SIPS_SUMMARY }
        ];
      }

      if (path.startsWith(ROUTES.SIPS_SIP_ID.split(':')[0])) {
        return [
          { label: 'Systematic Transactions', path: ROUTES.SIPS },
          { label: 'Active SIPs', path: ROUTES.SIPS_ACTIVE },
          { label: 'SIP Details', path: null }
        ];
      }

      if (path.startsWith(ROUTES.STP_ROOT + '/') && path !== ROUTES.STP_ACTIVE) {
        return [
          { label: 'Systematic Transactions', path: ROUTES.SIPS },
          { label: 'Active STPs', path: ROUTES.STP_ACTIVE },
          { label: 'STP Details', path: null }
        ];
      }

      if (path.startsWith(ROUTES.SWP_ROOT + '/') && path !== ROUTES.SWP_ACTIVE) {
        return [
          { label: 'Systematic Transactions', path: ROUTES.SIPS },
          { label: 'Active SWPs', path: ROUTES.SWP_ACTIVE },
          { label: 'SWP Details', path: null }
        ];
      }

      if (path === '/profile/personal-details') {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Personal Details', path: ROUTES.PROFILE_PERSONAL_DETAILS }
        ];
      }

      if (path === '/profile/nominee-details') {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Nominee Details', path: ROUTES.PROFILE_NOMINEE_DETAILS }
        ];
      }

      if (path === '/profile/bank-details') {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Bank Details', path: ROUTES.PROFILE_BANK_DETAILS }
        ];
      }

      if (path === '/profile/mandates') {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Mandates', path: ROUTES.PROFILE_MANDATES }
        ];
      }

      if (path === ROUTES.PROFILE_ADD_NOMINEE) {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Nominee Details', path: ROUTES.PROFILE_NOMINEE_DETAILS },
          { label: 'Add Nominee', path: null }
        ];
      }

      if (path === ROUTES.PROFILE_ADD_BANK_ACCOUNT) {
        return [
          { label: 'Profile', path: ROUTES.PROFILE },
          { label: 'Bank Details', path: ROUTES.PROFILE_BANK_DETAILS },
          { label: 'Add Bank Account', path: null }
        ];
      }

      if (path.startsWith('/explore/mf-collection-details/') && path !== '/explore/mf-collection-details') {
        return [
          { label: 'Explore', path: ROUTES.EXPLORE },
          { label: 'Collection Details', path: null }
        ];
      }

      if (path === '/notifications/general') {
        return [
          { label: 'Notifications', path: ROUTES.NOTIFICATIONS },
          { label: 'General', path: ROUTES.NOTIFICATIONS_GENERAL }
        ];
      }

      if (path === '/notifications/important') {
        return [
          { label: 'Notifications', path: ROUTES.NOTIFICATIONS },
          { label: 'Important', path: ROUTES.NOTIFICATIONS_IMPORTANT }
        ];
      }

      return [];
    };

    return getBreadcrumbsForPath(pathname);
  };

  const breadcrumbs = generateBreadcrumbs();

  // Debug logging
  console.log('BreadcrumbNavigation - Generated breadcrumbs:', breadcrumbs);

  if (breadcrumbs.length === 0) {
    console.log('BreadcrumbNavigation - No breadcrumbs found, returning null');
    return null;
  }

  return (
    <MuiBreadcrumbs
      separator={<ChevronRight sx={{ fontSize: 16, color: theme.palette.custom.text.helperDisabled }} />}
      sx={{
        mb: 1,
        py: 1,
        '& .MuiBreadcrumbs-separator': {
          mx: 1,
        },
      }}
    >
      {breadcrumbs.map((breadcrumb, index) => (
        <Box key={index}>
          {breadcrumb.path && index < breadcrumbs.length - 1 ? (
            <Link
              to={breadcrumb.path}
              style={{
                textDecoration: 'none',
                color: theme.palette.custom.text.helperDisabled,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: theme.palette.custom.text.helperDisabled,
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {breadcrumb.label}
              </Typography>
            </Link>
          ) : (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: theme.palette.custom.text.heading,
              }}
            >
              {breadcrumb.label}
            </Typography>
          )}
        </Box>
      ))}
    </MuiBreadcrumbs>
  );
};

export default BreadcrumbNavigation;
